import { Cell } from './Cell';
import { GridType } from './GridType';

export interface BaseGridConfig {
  rows: number;
  cols: number;
  gridType: GridType;
}

export abstract class BaseGrid {
  protected cells: Cell[][];
  public readonly rows: number;
  public readonly cols: number;
  public readonly gridType: GridType;

  protected startCell: Cell | null = null;
  protected goalCell: Cell | null = null;

  constructor(config: BaseGridConfig) {
    this.rows = config.rows;
    this.cols = config.cols;
    this.gridType = config.gridType;
    this.cells = this.initializeGrid();
  }

  protected initializeGrid(): Cell[][] {
    const grid: Cell[][] = [];
    for (let row = 0; row < this.rows; row++) {
      grid[row] = [];
      for (let col = 0; col < this.cols; col++) {
        grid[row][col] = new Cell(row, col);
      }
    }
    return grid;
  }

  abstract getNeighbors(cell: Cell): Cell[];
  abstract calculateDistance(cellA: Cell, cellB: Cell): number;

  getCell(row: number, col: number): Cell | null {
    if (this.isInBounds(row, col)) {
      return this.cells[row][col];
    }
    return null;
  }

  getCellAt(row: number, col: number): Cell {
    if (!this.isInBounds(row, col)) {
      throw new Error(`Cell at (${row}, ${col}) is out of bounds`);
    }
    return this.cells[row][col];
  }

  isInBounds(row: number, col: number): boolean {
    return row >= 0 && row < this.rows && col >= 0 && col < this.cols;
  }

  setStart(row: number, col: number): void {
    if (this.startCell) {
      this.startCell.type = 0;
    }
    const cell = this.getCellAt(row, col);
    if (cell.type === 3) {
      this.goalCell = null;
    }
    cell.type = 2;
    this.startCell = cell;
  }

  setGoal(row: number, col: number): void {
    if (this.goalCell) {
      this.goalCell.type = 0;
    }
    const cell = this.getCellAt(row, col);
    if (cell.type === 2) {
      this.startCell = null;
    }
    cell.type = 3;
    this.goalCell = cell;
  }

  setWall(row: number, col: number, isWall: boolean): void {
    const cell = this.getCellAt(row, col);
    if (cell.type === 2 || cell.type === 3) {
      return;
    }
    cell.type = isWall ? 1 : 0;
  }

  getStart(): Cell | null {
    return this.startCell;
  }

  getGoal(): Cell | null {
    return this.goalCell;
  }

  reset(): void {
    for (let row = 0; row < this.rows; row++) {
      for (let col = 0; col < this.cols; col++) {
        this.cells[row][col].reset();
      }
    }
  }

  clear(): void {
    this.startCell = null;
    this.goalCell = null;
    for (let row = 0; row < this.rows; row++) {
      for (let col = 0; col < this.cols; col++) {
        const cell = this.cells[row][col];
        cell.type = 0;
        cell.reset();
      }
    }
  }

  clearPath(): void {
    this.reset();
  }

  getAllCells(): Cell[] {
    const allCells: Cell[] = [];
    for (let row = 0; row < this.rows; row++) {
      for (let col = 0; col < this.cols; col++) {
        allCells.push(this.cells[row][col]);
      }
    }
    return allCells;
  }
}
